import { useState, useEffect, useRef, useCallback } from 'react';

const searchCache = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000;

const createCacheEntry = (data) => ({
  data,
  timestamp: Date.now(),
  expiry: Date.now() + CACHE_EXPIRY
});

const isCacheValid = (entry) => {
  return entry && Date.now() < entry.expiry;
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useGoogleSearch = (options = {}) => {
  const {
    minLength = 4,
    debounceDelay = 300,
    cacheExpiry = CACHE_EXPIRY,
    apiEndpoint = '/api/location/search_address',
    city = null
  } = options;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedQuery = useDebounce(query, debounceDelay);
  const abortControllerRef = useRef(null);
  // Text placed in the box by picking a suggestion; searching it again would
  // only spend another Places request and reopen the list.
  const selectedTextRef = useRef(null);

  const searchAddresses = useCallback(async (searchQuery) => {
    if (selectedTextRef.current !== null && searchQuery === selectedTextRef.current) {
      return;
    }

    if (!searchQuery || searchQuery.length < minLength) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    const cacheKey = `${searchQuery.toLowerCase().trim()}_${city || 'global'}`;
    const cachedEntry = searchCache.get(cacheKey);

    if (isCacheValid(cachedEntry)) {
      setResults(cachedEntry.data || []);
      setLoading(false);
      setError(null);
      setHasSearched(true);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {

      const url = new URL(`${process.env.REACT_APP_API_URL}/api/location/search_address`);
      url.searchParams.set('input', searchQuery);

      if (city && city.trim()) {
        url.searchParams.set('city', city.trim());
      }

      const response = await fetch(url.toString(), {
        signal: abortControllerRef.current.signal,
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.error) {
        throw new Error(data.error || `Address search failed (HTTP ${response.status})`);
      }

      const predictions = data.predictions || [];

      searchCache.set(cacheKey, createCacheEntry(predictions));

      setResults(predictions);
      setHasSearched(true);
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      console.error('Search error:', err);
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [minLength, apiEndpoint, city]);

  useEffect(() => {
    searchAddresses(debouncedQuery);
  }, [debouncedQuery, searchAddresses]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const clearCache = useCallback(() => {
    searchCache.clear();
    console.log('Search cache cleared');
  }, []);

  const getCacheStats = useCallback(() => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [key, entry] of searchCache.entries()) {
      if (isCacheValid(entry)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      total: searchCache.size,
      valid: validEntries,
      expired: expiredEntries
    };
  }, []);

  const cleanExpiredCache = useCallback(() => {
    const now = Date.now();
    for (const [key, entry] of searchCache.entries()) {
      if (!isCacheValid(entry)) {
        searchCache.delete(key);
      }
    }
    console.log('Expired cache entries cleaned');
  }, []);

  const updateQuery = useCallback((text) => {
    selectedTextRef.current = null;
    setQuery(text);
  }, []);

  const setSelectedText = useCallback((text) => {
    selectedTextRef.current = text;
    setQuery(text);
  }, []);

  return {
    query,
    setQuery: updateQuery,
    setSelectedText,
    results,
    loading,
    error,
    hasSearched,
    clearCache,
    getCacheStats,
    cleanExpiredCache,

    canSearch: query.length >= minLength,

    getResultText: (result) => result.description || result.formatted_address || '',

    getPlaceId: (result) => result.place_id || result.placeId || '',
  };
};

export default useGoogleSearch;
